import type {
  DsdEditorValueMatch,
  DsdEditorValueOccurrence,
} from "@/apis/dsd-editor/route";
import type { DsdEditorSelectedCellSummary } from "@/utils/dsd-editor/document-items";
import { getOccurrenceIds } from "@/utils/dsd-editor/note-reference-review";
import { DSD_REVIEW_COPY } from "../copy";
import { getOccurrenceAxisLabel } from "../note-reference-review-panel-ui";

/** 주석·주석매칭 후보 카드 하나 = 값이 같은 다른 셀(또는 문단 값) */
export type DsdReviewNoteMatchItem = {
  key: string;
  occurrence: DsdEditorValueOccurrence;
  title: string;
  axisLabel: string;
  value: string;
  /** 칩 — 선택 셀과 같은 주석 청크 안이면 "같은 주석"(같은 주석의 다른 표 등), 아니면 "다른 주석" */
  tagLabel: string;
  /** 값이 같은 후보만 오므로 늘 "금액 일치". 패널 카드와 근거 창 머리글이 이 값을 읽는다 */
  matchLabel: string;
};

export type DsdReviewNoteMatchView = {
  cellId: string;
  sectionName: string;
  axisLabel: string;
  value: string;
  items: DsdReviewNoteMatchItem[];
};

/**
 * 선택 셀의 패널 내용 — 셀 요약 + 값이 같은 후보 카드. 검토(판단)는 없다.
 * 본문·주석매칭처럼 패널은 match 의 source 셀을 설명한다 — 매칭이 양방향이라 어느 쪽 셀을 눌러도
 * 그 셀이 source 인 원소가 있어 선택 셀 = source 가 된다.
 */
export const buildDsdReviewNoteMatchView = ({
  selectedCellId,
  cellSummaryById,
  activeValueMatch,
}: {
  selectedCellId: string | null;
  cellSummaryById: ReadonlyMap<string, DsdEditorSelectedCellSummary>;
  activeValueMatch: DsdEditorValueMatch | null;
}): DsdReviewNoteMatchView | null => {
  if (!selectedCellId) return null;
  const source = activeValueMatch?.source ?? null;
  const sourceCellId =
    (source &&
      getOccurrenceIds(source).find((id) => cellSummaryById.has(id))) ??
    selectedCellId;
  const summary = cellSummaryById.get(sourceCellId);
  if (!summary) return null;

  return {
    cellId: selectedCellId,
    sectionName: summary.sectionName,
    axisLabel: source ? getOccurrenceAxisLabel(source) : "",
    value: summary.value,
    items: (activeValueMatch?.candidates ?? []).map(
      ({ occurrence }, index) => ({
        key: `${occurrence.id}:${index}`,
        occurrence,
        title: occurrence.chunkTitle || occurrence.chunkId,
        axisLabel: getOccurrenceAxisLabel(occurrence),
        value: occurrence.value,
        tagLabel:
          DSD_REVIEW_COPY.noteMatching.tag[
            source && occurrence.chunkId === source.chunkId ? "same" : "other"
          ],
        matchLabel: DSD_REVIEW_COPY.panel.matchLabel.value,
      }),
    ),
  };
};
